import React, { useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Avatar from "@mui/material/Avatar";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { setAddMembersModalVisibility } from "../../vcompSlice";
import Langs, { Localize } from "../../../../app/lang/langs";
import moment from "moment";

const SWIPE_THRESHOLD = 80;

const avatarColors = [
    "#FF9015", "#4CAF50", "#2196F3", "#9C27B0",
    "#00BCD4", "#FF5722", "#607D8B", "#E91E63",
    "#3F51B5", "#009688", "#795548", "#FF6F00",
];

function getAvatarColor(id) {
    return avatarColors[(id || 0) % avatarColors.length];
}

function getInitials(nom, prenom) {
    const first = (nom || "").charAt(0).toUpperCase();
    const second = (prenom || "").charAt(0).toUpperCase();
    return `${first}${second}` || "?";
}

/**
 * SwipeableCard — A member card that reveals a delete action on swipe left.
 */
function SwipeableCard({ member, onDelete, onToggleMcq, onViewResults }) {
    const [offsetX, setOffsetX] = useState(0);
    const [swiping, setSwiping] = useState(false);
    const startX = useRef(0);
    const currentX = useRef(0);

    const handleTouchStart = useCallback((e) => {
        startX.current = e.touches[0].clientX;
        currentX.current = startX.current;
        setSwiping(true);
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!swiping) return;
        currentX.current = e.touches[0].clientX;
        const diff = currentX.current - startX.current;
        // Only allow swipe left (negative)
        if (diff < 0) {
            setOffsetX(Math.max(diff, -120));
        } else {
            setOffsetX(0);
        }
    }, [swiping]);

    const handleTouchEnd = useCallback(() => {
        setSwiping(false);
        if (offsetX < -SWIPE_THRESHOLD) {
            setOffsetX(-100);
        } else {
            setOffsetX(0);
        }
    }, [offsetX]);

    const handleCloseSwipe = useCallback(() => {
        setOffsetX(0);
    }, []);

    const initials = getInitials(member.nom, member.prenom);
    const bgColor = getAvatarColor(member.id);
    const isMcqActive = member.active_mcq == 1;
    const hasResults = !!member.last_response_date;

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "14px",
                mb: 1.5,
            }}
        >
            {/* Delete action behind the card */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#ef5350",
                    borderRadius: "14px",
                    zIndex: 0,
                }}
                onClick={() => {
                    onDelete(member.id);
                    handleCloseSwipe();
                }}
            >
                <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>

            {/* Main card */}
            <Card
                elevation={0}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    transform: `translateX(${offsetX}px)`,
                    transition: swiping ? "none" : "transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                    bgcolor: "var(--surface-card, #fff)",
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor: "rgba(0,0,0,0.06)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)",
                    "&:active": {
                        transform: `translateX(${offsetX}px) scale(0.99)`,
                    },
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    {/* Top row: Avatar + Name + Delete */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                            sx={{
                                width: 44,
                                height: 44,
                                bgcolor: bgColor,
                                fontSize: "1rem",
                                fontWeight: 700,
                                letterSpacing: "0.5px",
                                flexShrink: 0,
                            }}
                        >
                            {initials}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    lineHeight: 1.3,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {member.nom} {member.prenom}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => onDelete(member.id)}
                            sx={{
                                color: "rgba(0,0,0,0.3)",
                                "&:hover": { color: "#ef5350" },
                                flexShrink: 0,
                            }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Bottom row: MCQ switch + Results */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 1,
                            ml: 7,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <QuizOutlinedIcon
                                sx={{ fontSize: 18, color: "rgba(0,0,0,0.4)" }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: "rgba(0,0,0,0.5)", fontWeight: 500, mr: 0.5 }}
                            >
                                MCQ
                            </Typography>
                            <Switch
                                size="small"
                                checked={isMcqActive}
                                onChange={(e) => onToggleMcq({ value: e.target.checked }, member)}
                                color="warning"
                            />
                        </Box>

                        {hasResults ? (
                            <Chip
                                label={moment(member.last_response_date).format("DD/MM/YYYY")}
                                size="small"
                                onClick={() =>
                                    onViewResults(
                                        member.id,
                                        member.last_responses_number,
                                        member.last_response_date
                                    )
                                }
                                sx={{
                                    bgcolor: "rgba(255,144,21,0.1)",
                                    color: "#FF9015",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    height: 28,
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor: "rgba(255,144,21,0.18)",
                                    },
                                }}
                                icon={
                                    <ChevronRightIcon
                                        sx={{ fontSize: 16, color: "#FF9015" }}
                                    />
                                }
                            />
                        ) : (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "rgba(0,0,0,0.35)",
                                    fontStyle: "italic",
                                    fontSize: "0.75rem",
                                }}
                            >
                                Pas de réponse
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Card>
        </Box>
    );
}

/**
 * MobileMembersTab — Mobile-optimized members list with swipe-to-delete,
 * MCQ toggles, and card-based layout.
 */
export default function MobileMembersTab({
    isMobile,
    mode,
    activeIndex,
    setActiveIndex,
    collabsList,
    dtCollabs,
    onConfirmDeleteCollab,
    onActivateCollabMcq,
    onActivateAllMcq,
    onViewMcqResults,
}) {
    const dispatch = useDispatch();

    const allActive = collabsList.length > 0 && collabsList.every((c) => c.active_mcq === 1);

    const showPrevious = mode !== "edit";

    return (
        <Box
            sx={{
                mt: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                bgcolor: "rgba(0,0,0,0.02)",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2,
                    pt: 2,
                    pb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "var(--surface-card, #fff)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            color: "rgba(0,0,0,0.85)",
                        }}
                    >
                        <Langs str="EDIT_PROJECT_MEMBERS" />
                    </Typography>
                    <Chip
                        label={collabsList.length}
                        size="small"
                        sx={{
                            bgcolor: "#FF9015",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            height: 24,
                            minWidth: 24,
                        }}
                    />
                </Box>
                <IconButton
                    onClick={() => dispatch(setAddMembersModalVisibility(true))}
                    sx={{
                        bgcolor: "rgba(255,144,21,0.1)",
                        color: "#FF9015",
                        width: 44,
                        height: 44,
                        "&:hover": {
                            bgcolor: "rgba(255,144,21,0.2)",
                        },
                        "&:active": {
                            transform: "scale(0.95)",
                        },
                    }}
                >
                    <PersonAddIcon />
                </IconButton>
            </Box>

            {/* Toggle All MCQ */}
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.06)",
                        bgcolor: "var(--surface-card, #fff)",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <QuizOutlinedIcon
                                sx={{ fontSize: 20, color: "#FF9015" }}
                            />
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "rgba(0,0,0,0.7)" }}
                            >
                                {Localize("MCQ") || "MCQ"} — {Localize("Activer tout") || "Activer tout"}
                            </Typography>
                        </Box>
                        <Switch
                            checked={allActive}
                            onChange={(e) => onActivateAllMcq({ value: e.target.checked })}
                            color="warning"
                        />
                    </Box>
                </Card>
            </Box>

            {/* Members List */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    px: 2,
                    pt: 1,
                    pb: 12,
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {collabsList.length === 0 ? (
                    /* Empty State */
                    <Fade in timeout={400}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                                px: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(255,144,21,0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 2.5,
                                }}
                            >
                                <PeopleOutlineIcon
                                    sx={{ fontSize: 40, color: "rgba(255,144,21,0.5)" }}
                                />
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: "rgba(0,0,0,0.6)",
                                    mb: 0.5,
                                    textAlign: "center",
                                }}
                            >
                                Aucun membre
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "rgba(0,0,0,0.4)",
                                    textAlign: "center",
                                    maxWidth: 260,
                                    lineHeight: 1.5,
                                }}
                            >
                                Ajoutez des membres à ce projet en utilisant le bouton ci-dessus
                            </Typography>
                        </Box>
                    </Fade>
                ) : (
                    collabsList.map((member, index) => (
                        <Fade
                            key={member.id}
                            in
                            timeout={300 + index * 60}
                            style={{ transitionDelay: `${index * 40}ms` }}
                        >
                            <Box>
                                <SwipeableCard
                                    member={member}
                                    onDelete={onConfirmDeleteCollab}
                                    onToggleMcq={onActivateCollabMcq}
                                    onViewResults={onViewMcqResults}
                                />
                            </Box>
                        </Fade>
                    ))
                )}
            </Box>

            {/* Sticky Bottom Navigation */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: 2.5,
                    py: 2,
                    pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                    background: "linear-gradient(to top, rgba(255,255,255,0.92) 50%, rgba(255,255,255,0.78) 100%)",
                    backdropFilter: "blur(18px) saturate(1.6)",
                    WebkitBackdropFilter: "blur(18px) saturate(1.6)",
                    borderTop: "1px solid rgba(255, 144, 21, 0.15)",
                    boxShadow: "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.04)",
                    display: "flex",
                    gap: 1.5,
                    zIndex: 10,
                }}
            >
                {showPrevious && (
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setActiveIndex(activeIndex - 1)}
                        startIcon={<ChevronLeftIcon />}
                        sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            minHeight: 50,
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            letterSpacing: "-0.01em",
                            border: "1.5px solid rgba(0, 0, 0, 0.12)",
                            color: "#4b5263",
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                borderColor: "rgba(255, 144, 21, 0.35)",
                                backgroundColor: "rgba(255, 144, 21, 0.06)",
                                color: "#FF9015",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="Previous" />
                    </Button>
                )}
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setActiveIndex(activeIndex + 1)}
                    endIcon={<ChevronRightIcon />}
                    sx={{
                        borderRadius: "14px",
                        textTransform: "none",
                        minHeight: 50,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        letterSpacing: "-0.01em",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                        backgroundColor: "#FF9015",
                        color: "#fff",
                        border: "none",
                        boxShadow: "0 4px 16px rgba(255, 144, 21, 0.40), 0 8px 32px rgba(255, 144, 21, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                        transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #FF9015 0%, #FFB74D 100%)",
                            backgroundColor: "#FF9015",
                            boxShadow: "0 6px 24px rgba(255, 144, 21, 0.50), 0 12px 40px rgba(255, 144, 21, 0.25)",
                            transform: "translateY(-2px)",
                        },
                        "&:active": {
                            transform: "scale(0.98)",
                            boxShadow: "0 2px 10px rgba(255, 144, 21, 0.35)",
                        },
                    }}
                >
                    <Langs str="Next" />
                </Button>
            </Box>
        </Box>
    );
}
